/**
 * 路由参数解析工具（vue-router query/path ↔ SDK URLCharacterParams）
 *
 * 支持两种 URL 格式：
 *   - 纯 query: #/viewer2?sceneUrl=https://...&showDebugViewer=true（普通场景 URL）
 *   - siteUri catch-all: #/world://www.nobody.com/castle.site?showDebugViewer=true（.site 结尾的场所 URI）
 *
 * @changelog
 * | 版本   | 变更说明                             | 关联                                    |
 * | v1.0.0 | 初始实现：抽出 params → URLCharacterParams 解析 | REQ: h5-query-to-params-迁移            |
 * | v1.1.0 | 新增 segments key/value 格式解析 + parseURLParamsFromQuery | REQ: h5-query-to-params-迁移  |
 * | v1.2.0 | sceneUrl 从 key/value 对改为 segments 首位直出（去掉 sceneUrl key） | REQ: h5-query-to-params-迁移 |
 * | v1.3.0 | 支持未编码 sceneUrl：按首个已知 key 边界从 hash/segments 识别完整 URL | REQ: h5-query-to-params-迁移 |
 * | v1.4.0 | 构建路由时支持 sceneUrl 自动编码，同时兼容已编码/未编码入链 | REQ: h5-query-to-params-迁移 |
 * | v2.0.0 | sceneUrl 保留为路径参数，其余参数改为 query 格式 | REQ: h5-query-to-params-迁移 |
 * | v3.0.0 | 移除老 key/value segments 格式，仅保留 sceneUrl 路径 + query | REQ: h5-query-to-params-迁移 |
 * | v4.0.0 | 移除 path+query 格式（#/viewer2/https://...），普通 URL 走纯 query，siteUri 走 catch-all path | REQ: h5-query-to-params-迁移 |
 * | v4.1.0 | 新增 computeViewer2HashRedirect：非 hash 模式 /viewer2?xxx → /#/viewer2?xxx 重定向 | REQ: h5-query-to-params-迁移 |
 * | v4.2.0 | getSceneConfigUrl 改为引用 config/scene-api 集中常量，修正生产 URL 路径 typo（api_wuji_cache → api/wuji_cache） | REQ: 消除重复 URL |
 * | v4.3.0 | getSceneConfigUrl 去掉 ?t=Date.now() 时间戳（每次调用 URL 不同导致 COS 边缘节点多副本 CORS 头不一致偶现失败）；改由 fetch 层 cache:'no-cache' + Cache-Control header 防缓存 | REQ: 修复偶现 CORS |
 * | v5.0.0 | 数据源迁移：getSceneConfigUrl 统一返回 COS 静态 JSON URL，移除无极 API 和环境判断分支 | REQ: 场景列表COS化    |
 *
 * @author v_djlzhou
 */
import type { RouteLocationNormalizedLoaded } from 'vue-router';
import type { URLCharacterParams } from '@tencent/viewer-sdk';

import { SCENE_CONFIG_URL } from '../config/scene-api.ts';

type RouteParamMap = Record<string, unknown>;

const ROUTE_BASE_PATHS = ['/viewer2/', '/debug-viewer/', '/creative/'];

/** 安全解码单个字符串 param（undefined/'' → ''） */
export function decodeParam(value: unknown): string {
  if (value === undefined || value === null || value === '') return '';
  try {
    return decodeURIComponent(String(value));
  } catch {
    // decodeURIComponent 遇到非法转义（如单独的 %）会抛错，此时降级返回原始字符串
    return String(value);
  }
}

/** 安全解析 JSON 数组 param；解析失败/空值均返回 [] */
export function parseJsonArrayParam<T = unknown>(value: unknown): T[] {
  if (value === undefined || value === null || value === '') return [];
  try {
    const decoded = decodeURIComponent(String(value));
    const parsed = JSON.parse(decoded);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch (err) {
    console.warn('[route-params] JSON 数组解析失败，已兜底为 []:', value, err);
    return [];
  }
}

/**
 * 将路径 segments 转成参数对象。
 * 新格式：所有 segments 拼接为 sceneUrl。
 */
export function parseRouteSegments(segments: unknown): RouteParamMap {
  const list = Array.isArray(segments) ? segments.map(String) : typeof segments === 'string' ? [segments] : [];
  if (list.length === 0) return {};
  const sceneUrl = list.join('/');
  return sceneUrl ? { sceneUrl } : {};
}

/**
 * 构建路由位置对象（segments + query），用于 router.push({ params: { segments }, query })。
 *
 * 自动判断格式：
 * - sceneUrl 以 .site 结尾 → 放在路径 segments，走 catch-all 路由（#/world://...site?params）
 * - 普通 sceneUrl → 放在 query（#/viewer2?sceneUrl=...&params）
 */
export function buildRouteLocation(params: RouteParamMap): {
  segments: string[];
  query: Record<string, string>;
} {
  const segments: string[] = [];
  const query: Record<string, string> = {};

  const sceneUrl = params.sceneUrl != null && params.sceneUrl !== ''
    ? String(params.sceneUrl) : '';

  if (sceneUrl && sceneUrl.endsWith('.site')) {
    // siteUri 放在路径 segments，走 catch-all 路由
    segments.push(...sceneUrl.split('/'));
  } else if (sceneUrl) {
    // 普通 sceneUrl 放在 query
    query.sceneUrl = sceneUrl;
  }

  // 其余参数放在 query
  for (const [key, value] of Object.entries(params)) {
    if (key === 'sceneUrl') continue;
    if (value === undefined || value === null || value === '') continue;
    query[key] = String(value);
  }

  return { segments, query };
}

function parseRouteParamsFromHash(): RouteParamMap {
  const hash = window.location.hash;
  if (!hash) return {};

  // 按 ? 分割 path 和 query
  const qIndex = hash.indexOf('?');
  const hashPath = (qIndex >= 0 ? hash.slice(0, qIndex) : hash).replace(/^#/, '');
  const hashQuery = qIndex >= 0 ? hash.slice(qIndex + 1) : '';

  // 已知 base path（/viewer2, /debug-viewer, /creative）→ sceneUrl 从 query 取
  const isKnownBasePath = ROUTE_BASE_PATHS.some((path) =>
    hashPath.startsWith(path) || hashPath === path.slice(0, -1),
  );

  let sceneUrl = '';
  if (!isKnownBasePath && hashPath && hashPath !== '/') {
    // siteUri catch-all：#/world://...site（不带 /viewer2 等前缀）
    sceneUrl = hashPath.replace(/^\//, '');
  }

  // 解析 query 参数
  const queryParams: RouteParamMap = {};
  if (hashQuery) {
    const params = new URLSearchParams(hashQuery);
    for (const [key, value] of params.entries()) {
      queryParams[key] = value;
    }
  }

  const result: RouteParamMap = { ...queryParams };
  if (sceneUrl) result.sceneUrl = sceneUrl;
  return result;
}

/**
 * 读取 route.params 中的业务参数。
 * 新格式优先手动解析 hash（sceneUrl 在 path，其余在 query）；
 * 无 hash 时回退到 vue-router params + query。
 */
export function getRouteParamMap(route: RouteLocationNormalizedLoaded): RouteParamMap {
  const hashParams = parseRouteParamsFromHash();
  if (Object.keys(hashParams).length > 0 && hashParams.sceneUrl) return hashParams;

  // 回退：segments 提供 sceneUrl，query 提供其他参数
  const segmentParams = parseRouteSegments(route.params.segments);
  const queryMap: RouteParamMap = {};
  const routeQuery = (route as { query?: Record<string, unknown> }).query;
  if (routeQuery && typeof routeQuery === 'object') {
    for (const [key, value] of Object.entries(routeQuery)) {
      if (value !== null && value !== undefined) {
        queryMap[key] = Array.isArray(value) ? String(value[0]) : String(value);
      }
    }
  }
  if (segmentParams.sceneUrl) {
    return { ...segmentParams, ...queryMap };
  }
  return { ...queryMap, ...route.params };
}

/**
 * 场景配置拉取地址，与 ModelList.vue 共用逻辑。
 *
 * v2.0.0：统一从 COS 静态 JSON 读取，不再区分域名/环境。
 */
export function getSceneConfigUrl(): string {
  return SCENE_CONFIG_URL;
}

/**
 * fetch 场景配置的推荐调用方式：稳定 URL + cache:'no-cache' 防缓存。
 * 调用方（ModelList.vue / resolveSceneUrlFromSiteUri）统一走此包装，避免各自遗漏 cache 选项。
 *
 * v5.1.0：移除 `Cache-Control: no-cache` 请求头。
 * 原因：该头不属于 CORS 简单头，会触发浏览器 preflight（OPTIONS 预检），
 * 导致每次加载场景列表都发两次请求。`cache: 'no-cache'` fetch 选项已足够
 * 控制浏览器缓存（条件请求 + 304 校验），无需额外请求头。
 */
export function fetchSceneConfig(): Promise<Response> {
  return fetch(getSceneConfigUrl(), {
    cache: 'no-cache',
  });
}

/**
 * 将 siteUri（.site 结尾）解析为真实场景 URL。
 * 从场景配置中匹配 siteUri / siteUrl / uri / site 字段，返回对应的 modelUrlV2 或 modelUrl。
 */
export async function resolveSceneUrlFromSiteUri(siteUri: string): Promise<string> {
  try {
    const res = await fetchSceneConfig();
    if (res.status !== 200) return siteUri;
    const content = await res.json();
    const list = Array.isArray(content) ? content : content.data;
    const scene = Array.isArray(list)
      ? list.find((s: Record<string, unknown>) =>
          String(s.siteUri || s.siteUrl || s.uri || s.site || '') === siteUri)
      : undefined;
    return (scene?.modelUrlV2 || scene?.modelUrl || siteUri) as string;
  } catch {
    return siteUri;
  }
}

/** 判断当前路由是否使用新 params/segments 格式 */
export function hasNewRouteParams(route: RouteLocationNormalizedLoaded): boolean {
  const p = getRouteParamMap(route);
  return p.sceneUrl !== undefined && p.sceneUrl !== '';
}

/** 从 URL query 中解析 showDebugViewer（兼容老链接 ?showDebugViewer=true） */
function parseShowDebugViewerFromQuery(): boolean {
  const searchQuery = (window.location.search || '').replace(/^\?/, '');
  const hashQ = window.location.hash.indexOf('?');
  const hashQuery = hashQ >= 0 ? window.location.hash.slice(hashQ + 1) : '';
  const merged = [searchQuery, hashQuery].filter(Boolean).join('&');
  const params = new URLSearchParams(merged);
  return params.get('showDebugViewer') === 'true';
}

/** 是否展示 Debug Viewer（新格式 route params 优先，老格式回退到 query） */
export function getShowDebugViewerFromRoute(route: RouteLocationNormalizedLoaded): boolean {
  const p = getRouteParamMap(route);
  if (p.showDebugViewer !== undefined) return p.showDebugViewer === 'true';
  return parseShowDebugViewerFromQuery();
}

/**
 * 将 route.params 转成 URLCharacterParams。
 * 调用方需先用 hasNewRouteParams 判断，若返回 false 应调用 parseURLParamsFromQuery() 兼容老链接。
 */
export function buildURLParamsFromRoute(route: RouteLocationNormalizedLoaded): URLCharacterParams {
  const p = getRouteParamMap(route);
  return {
    sceneUrl: decodeParam(p.sceneUrl),
    avatarUrl: decodeParam(p.avatarUrl),
    height: decodeParam(p.height),
    characterId: p.characterId ? String(p.characterId) : '',
    aniList: parseJsonArrayParam(p.aniList),
    modelTexture: parseJsonArrayParam(p.modelTexture),
    thirdPartyUrl: p.thirdPartyUrl ? decodeParam(p.thirdPartyUrl) : undefined,
  };
}

/**
 * 从 URL query（search / hash）中解析参数，兼容老链接 ?key=value 格式。
 *
 * 原逻辑在 SDK ViewerSDK.parseURLParams() 中，现已迁移至 H5 层，
 * SDK 不再直接读取 window.location。
 */
export function parseURLParamsFromQuery(): URLCharacterParams {
  const searchQuery = (window.location.search || '').replace(/^\?/, '');
  const hashQ = window.location.hash.indexOf('?');
  const hashQuery = hashQ >= 0 ? window.location.hash.slice(hashQ + 1) : '';
  const merged = [searchQuery, hashQuery].filter(Boolean).join('&');
  const params = new URLSearchParams(merged);

  const sceneUrl = params.get('sceneUrl') || '';
  const avatarUrl = params.get('avatarUrl') || '';
  const height = params.get('height') || '';
  const characterId = params.get('characterId') || '';

  let aniList: URLCharacterParams['aniList'] = [];
  const aniListStr = params.get('aniList');
  if (aniListStr) {
    try { aniList = JSON.parse(aniListStr); } catch { /* 兜底 [] */ }
  }

  let modelTexture: URLCharacterParams['modelTexture'] = [];
  const modelTextureStr = params.get('modelTexture');
  if (modelTextureStr) {
    try { modelTexture = JSON.parse(modelTextureStr); } catch { /* 兜底 [] */ }
  }

  const thirdPartyUrl = params.get('thirdParty') || params.get('third-party') || undefined;

  return { sceneUrl, avatarUrl, height, characterId, aniList, modelTexture, thirdPartyUrl };
}

/* ---------- 额外配置参数（非角色/场景，属于 SDK 初始化行为） ---------- */

export interface ExtraSDKParams {
  showBack?: string;
  showInteractionZone?: boolean;
  maxPagedSplats?: string;
  lodSplatCount?: string;
}

/** 从 route 的 segments/params 中提取额外 SDK 配置 */
export function getExtraParamsFromRoute(route: RouteLocationNormalizedLoaded): ExtraSDKParams {
  const p = getRouteParamMap(route);
  return {
    showBack: p.showBack ? String(p.showBack) : undefined,
    showInteractionZone: p.showInteractionZone === 'true' ? true : undefined,
    maxPagedSplats: p.maxPagedSplats ? String(p.maxPagedSplats) : undefined,
    lodSplatCount: p.lodSplatCount ? String(p.lodSplatCount) : undefined,
  };
}

/** 从 URL query 中解析额外 SDK 配置（兼容老链接 ?key=value） */
export function parseExtraParamsFromQuery(): ExtraSDKParams {
  const searchQuery = (window.location.search || '').replace(/^\?/, '');
  const hashQ = window.location.hash.indexOf('?');
  const hashQuery = hashQ >= 0 ? window.location.hash.slice(hashQ + 1) : '';
  const merged = [searchQuery, hashQuery].filter(Boolean).join('&');
  const params = new URLSearchParams(merged);

  return {
    showBack: params.get('showBack') || undefined,
    showInteractionZone: params.get('showInteractionZone') === 'true' ? true : undefined,
    maxPagedSplats: params.get('maxPagedSplats') || undefined,
    lodSplatCount: params.get('lodSplatCount') || undefined,
  };
}

/* ---------- 非 hash 模式 /viewer2 重定向 ---------- */

/**
 * 计算 /viewer2 非 hash 路径到 hash 模式的重定向目标。
 *
 * 当用户直接访问 /viewer2?sceneUrl=xxx（无 #）时，
 * 需要重定向到 /#/viewer2?sceneUrl=xxx（hash 模式）。
 * 仅对 /viewer2 路径生效，已有 hash 路由时跳过。
 *
 * @returns 重定向目标相对 URL（如 '/#/viewer2?sceneUrl=xxx'），无需重定向则返回 null
 */
export function computeViewer2HashRedirect(pathname: string, search: string, hash: string): string | null {
  // 已有 hash 路由则不处理
  if (hash && hash !== '#' && hash !== '#/') return null;

  // 查找 /viewer2 作为路径段（后面紧跟 / 或字符串结尾，避免误匹配 /viewer2abc）
  const match = pathname.match(/\/viewer2(\/|$)/);
  if (!match || match.index === undefined) return null;

  const basePath = pathname.slice(0, match.index);
  const rest = pathname.slice(match.index + '/viewer2'.length);
  const normalizedBase = basePath === '' ? '/' : basePath + '/';

  return `${normalizedBase}#/viewer2${rest}${search}`;
}

